import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';


export default function SearchableSelect({
  value,
  onChange,
  options = [],
  emptyOption = null,
  className = '',
  searchPlaceholder = 'Type to search...',
  placeholder = 'Select',
  disabled = false,
  loading = false,
  loadingLabel = 'Loading...',
  searchMinOptions = 0,
  id,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e) => {
      if (triggerRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      close();
    };
    // The panel is fixed-position, so close it rather than let it drift on scroll.
    const onScroll = (e) => {
      if (!panelRef.current?.contains(e.target)) close();
    };
    document.addEventListener('mousedown', onOutside);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const items = options.map((o) =>
    o !== null && typeof o === 'object'
      ? { value: o.value, label: o.label ?? String(o.value), searchText: o.searchText }
      : { value: o, label: String(o) }
  );

  const all = emptyOption ? [emptyOption, ...items] : items;
  const selected = all.find((o) => String(o.value) === String(value));
  // Nothing chosen yet: no value, or the blank "Select …" row. Shown grey as a
  // placeholder. A real choice — including a filter's "All" — shows dark.
  const isEmpty =
    value === undefined || value === null || String(value) === '' || !selected;

  const term = query.trim().toLowerCase();
  const filtered = term
    ? items.filter((o) => `${o.label} ${o.searchText || ''}`.toLowerCase().includes(term))
    : items;

  const isDisabled = disabled || loading;
  const showSearch = items.length >= searchMinOptions;

  const toggle = () => {
    if (isDisabled) return;
    if (open) {
      close();
      return;
    }
    const r = triggerRef.current.getBoundingClientRect();
    setCoords({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 180) });
    setOpen(true);
  };

  const pick = (v) => {
    onChange(v === null || v === undefined ? '' : String(v));
    close();
  };

  const rowCls = (v) =>
    `px-3 py-2 text-xs cursor-pointer hover:bg-primary-50 ${
      String(v) === String(value) ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700'
    }`;

  return (
    <>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        onClick={toggle}
        disabled={isDisabled}
        className={`${className} flex items-center justify-between gap-1 text-left ${loading ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        <span className={`truncate ${isEmpty || loading ? 'text-gray-400' : 'text-gray-800'}`}>
          {loading ? loadingLabel : (isEmpty ? (selected ? selected.label : placeholder) : selected.label)}
        </span>
        <ChevronDown
          size={13}
          className={`shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{ position: 'fixed', top: coords.top, left: coords.left, width: coords.width, zIndex: 9999 }}
            className="bg-white border border-gray-200 rounded-lg shadow-lg flex flex-col max-h-64"
          >
            {showSearch && (
            <div className="relative border-b border-gray-100">
              <Search
                size={12}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full h-8 pl-7 pr-2.5 text-xs outline-none rounded-t-lg"
              />
            </div>
            )}
            <div className="overflow-auto">
              {emptyOption && (
                <div onClick={() => pick(emptyOption.value)} className={rowCls(emptyOption.value)}>
                  {emptyOption.label}
                </div>
              )}
              {filtered.length === 0 && (
                <div className="px-3 py-2 text-xs text-gray-400">No matches</div>
              )}
              {filtered.map((o) => (
                <div key={String(o.value)} onClick={() => pick(o.value)} className={rowCls(o.value)}>
                  {o.label}
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}